import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  SITE_NAME,
  ORGANIZATION,
  ROUTES,
  resolveMeta,
  canonicalFor,
  imageFor,
} from "@/seo/seoData.mjs";
import { CATEGORY_PATHS } from "@/seo/categoryPages.mjs";

// Presentational head manager. Used by SeoManager (route config) and directly
// by dynamic pages like blog posts that carry their own metadata. All data
// lives in @/seo/seoData.mjs, which the build-time prerender script also
// reads — static HTML and client-rendered head share one source of truth.
export const Seo = ({ title, description, path, noindex = false, image, jsonld = [] }) => {
  const canonical = canonicalFor(path);
  const ogImage = imageFor(image);
  const schemas = [ORGANIZATION, ...jsonld];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <link rel="canonical" href={canonical} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@nischintcare" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export const SeoManager = () => {
  const { pathname } = useLocation();

  // Remove the static/prerendered fallback tags once the app boots, so
  // Helmet's per-route tags are the only set in the rendered document.
  useEffect(() => {
    document.querySelectorAll(".seo-static").forEach((el) => el.remove());
  }, []);

  // Individual blog posts, category landing pages and invite fallback pages
  // render their own <Seo> (they carry their own metadata / noindex).
  if (
    pathname.startsWith("/blog/") ||
    pathname.startsWith("/invite") ||
    CATEGORY_PATHS.includes(pathname)
  )
    return null;

  // Unknown paths render the catch-all NotFoundPage, which owns its head
  // (noindex). Without this skip it would fight the default home meta.
  if (!(pathname in ROUTES)) return null;

  const meta = resolveMeta(pathname);
  return <Seo {...meta} path={pathname} />;
};
